import { ChangeEvent, useCallback, useEffect, useState } from 'react';
import {
  TextField,
  Select,
  Checkbox,
  MenuItem,
  InputLabel,
  FormControl,
  SelectChangeEvent,
} from '@mui/material';
import { Box } from '@mui/system';
import debounce from '@mui/utils/debounce';
import { StyledTextField } from 'components/Inputs';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { InvoiceTab } from 'root/pathnames';
import { allocationProgress } from '../InvoicesTable/invoiceTable.const';
import {
  AllocationStatus,
  FiltersProps,
  InvoiceStatus,
  PaymentStatus,
} from './filters.types';

export const Filters = ({ params, setParams }: FiltersProps) => {
  const { tab: invoiceTab } = useParams<{ tab: InvoiceTab }>();
  const { t } = useTranslation(['misc', 'invoice', 'reports']);

  const {
    own,
    startDate,
    endDate,
    np,
    onlyMine,
    status,
    invoiceStatus,
    allocationStatus,
    administrative,
    filterText,
  } = params;

  const [searchValue, setSearchValue] = useState(filterText);

  const handleDateChange =
    (filterType: string) => (event: ChangeEvent<HTMLInputElement>) => {
      if (event.target.value) {
        setParams((prev) => ({
          ...prev,
          [filterType]: new Date(event.target.value),
        }));
      }
    };

  const handleSelectChange =
    (filterType: string) => (event: SelectChangeEvent<any>) =>
      setParams((prev) => ({
        ...prev,
        [filterType]: event.target.value,
      }));

  const debounceSearch = useCallback(
    debounce(
      (value: string) => setParams((prev) => ({ ...prev, filterText: value })),
      500,
    ),
    [invoiceTab],
  );

  const handleSearch = (e: ChangeEvent<HTMLInputElement>) => {
    setSearchValue(e.target.value);
    debounceSearch(e.target.value);
  };

  useEffect(() => {
    setSearchValue(filterText || '');
  }, [invoiceTab]);

  return (
    <Box
      display='flex'
      justifyContent='space-between'
      paddingBottom={!own ? 1.5 : 0}
      paddingX={2.25}
      paddingTop={2.25}
      minWidth='600px'
      style={{ fontSize: '0.925rem' }}
    >
      <div>
        <Box>
          <StyledTextField
            id='date'
            size='small'
            label={t('startDate', { ns: 'misc' })}
            type='date'
            width={170}
            onChange={handleDateChange('startDate')}
            value={startDate!.toISOString().slice(0, 10)}
            InputLabelProps={{
              shrink: true,
            }}
          />
          <StyledTextField
            id='date'
            size='small'
            label={t('endDate', { ns: 'misc' })}
            type='date'
            onChange={handleDateChange('endDate')}
            value={endDate!.toISOString().slice(0, 10)}
            width={170}
            sx={{ marginLeft: '10px' }}
            InputLabelProps={{
              shrink: true,
            }}
          />
          {own && !administrative && (
            <>
              <Checkbox
                id='NP'
                size='small'
                onChange={() =>
                  setParams((prev) => ({ ...prev, np: !prev.np }))
                }
                checked={np}
              />
              <label htmlFor='NP'>{t('np', { ns: 'invoice' })}</label>
            </>
          )}
          {!own && !administrative && (
            <FormControl sx={{ marginLeft: '20px', minWidth: 125 }}>
              <InputLabel id='allocation-status-label'>
                {t('allocationStatusLabel', { ns: 'invoice' })}
              </InputLabel>
              <Select
                label={t('allocationStatusLabel', { ns: 'invoice' })}
                value={allocationStatus}
                size='small'
                onChange={handleSelectChange('allocationStatus')}
              >
                <MenuItem value={AllocationStatus.ALL}>
                  {t('allocationProgress.ALL', { ns: 'invoice' })}
                </MenuItem>
                <MenuItem value={AllocationStatus.INITIAL}>
                  {allocationProgress.INITIAL}
                </MenuItem>
                <MenuItem value={AllocationStatus.PARTIAL}>
                  {allocationProgress.PARTIAL}
                </MenuItem>
                <MenuItem value={AllocationStatus.FULLY_ALLOCATED}>
                  {allocationProgress.FULL}
                </MenuItem>
                <MenuItem value={AllocationStatus.ACCEPTED}>
                  {allocationProgress.ACCEPTED}
                </MenuItem>
                {invoiceTab !== InvoiceTab.INTERNAL && (
                  <MenuItem value={AllocationStatus.FINALIZED}>
                    {allocationProgress.FINALIZED}
                  </MenuItem>
                )}
                <MenuItem value={AllocationStatus.CANCELLED}>
                  {allocationProgress.CANCELLED}
                </MenuItem>
                <MenuItem value={AllocationStatus.COMPLAINED}>
                  {allocationProgress.COMPLAINED}
                </MenuItem>
              </Select>
            </FormControl>
          )}
          {(administrative || own) && (
            <>
              <FormControl sx={{ marginLeft: '20px', minWidth: 125 }}>
                <Select
                  value={invoiceStatus}
                  size='small'
                  onChange={handleSelectChange('invoiceStatus')}
                >
                  <MenuItem value={InvoiceStatus.ALL}>
                    {t('invoicePublished.label', { ns: 'invoice' })}
                  </MenuItem>
                  <MenuItem value={InvoiceStatus.EXPORTED}>
                    {t('invoicePublished.EXPORTED', { ns: 'invoice' })}
                  </MenuItem>
                  <MenuItem value={InvoiceStatus.PUBLISHED}>
                    {t('invoicePublished.READY', { ns: 'invoice' })}
                  </MenuItem>
                  <MenuItem value={InvoiceStatus.NOT_PUBLISHED}>
                    {t('invoicePublished.DRAFT', { ns: 'invoice' })}
                  </MenuItem>
                </Select>
              </FormControl>
              <FormControl sx={{ marginLeft: '20px' }} size='small'>
                <InputLabel id='Status-label'>
                  {t('invoice.status.label', { ns: 'reports' })}
                </InputLabel>
                <Select
                  labelId='Status-label'
                  value={status}
                  onChange={handleSelectChange('status')}
                  label={t('invoice.status.label', { ns: 'reports' })}
                  sx={{ minWidth: '100px', color: 'black' }}
                >
                  <MenuItem value={PaymentStatus.ALL}>
                    {t('invoicePublished.label', { ns: 'invoice' })}
                  </MenuItem>
                  <MenuItem value={PaymentStatus.PAID}>
                    {t('invoiceStatus.PAID', { ns: 'invoice' })}
                  </MenuItem>
                  <MenuItem value={PaymentStatus.REQUIRE_PAYMENT}>
                    {t('invoiceStatus.NOT_PAID', { ns: 'invoice' })}
                  </MenuItem>
                </Select>
              </FormControl>
            </>
          )}
        </Box>

        {own && (
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Checkbox
              id='onlyMine'
              sx={{ ml: -1.125, mt: 0.5, p: 0.75 }}
              onChange={() =>
                setParams((prev) => ({ ...prev, onlyMine: !prev.onlyMine }))
              }
              checked={!!onlyMine}
              size='small'
            />
            <label htmlFor='onlyMine'>{t('onlyMine', { ns: 'misc' })}</label>
          </Box>
        )}
      </div>
      <TextField
        placeholder={t('button.search', { ns: 'misc' })}
        type='text'
        size='small'
        value={searchValue}
        onChange={handleSearch}
      />
    </Box>
  );
};
